import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { KINDS } from "@/lib/kinds";
import { useGroups, useOptions } from "@/hooks/useDirectory";
import { OptionPicker } from "@/components/common/OptionPicker";

const empty = { name: "", type: "", description: "", color: "", email: "", image_url: "", featured: false, visible: true, status: "Actif", parent: "" };

export function GroupFormDialog({ kind, group, open, onOpenChange }) {
  const meta = KINDS[kind];
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const options = useOptions(open).data?.ecosystem || {};
  const { data: groups } = useGroups();
  const parents = Object.values(groups || {}).flat().filter((g) => g.id !== group?.id);

  useEffect(() => {
    if (!open) return;
    setForm(group ? {
      name: group.name || "", type: group.type || "", description: group.description || "", color: group.color_raw || "",
      email: group.email || "", image_url: group.image || "", featured: group.featured, visible: group.visible,
      status: group.status || "Actif", parent: group.parent?.id || "",
    } : { ...empty, type: kind === "groupes" ? "Sous-groupe" : "" });
  }, [open, group, kind]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Le nom est obligatoire.");
    setSaving(true);
    const { parent, ...payload } = form;
    payload.parent_ids = parent ? [parent] : [];
    if (!payload.type) delete payload.type;
    if (group && payload.image_url === (group.image || "")) delete payload.image_url;
    try {
      if (group) await api.patch(`/groups/${kind}/${group.id}`, payload);
      else await api.post(`/groups/${kind}`, payload);
      toast.success(group ? "Modifications enregistrées dans Notion" : `${meta.singular} créé(e) dans Notion`);
      ["groups", "group", "featured", "stats"].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      onOpenChange(false);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const field = (id, label, input) => <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label>{input}</div>;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="group-form-dialog">
        <DialogHeader>
          <DialogTitle>{group ? `Modifier « ${group.name} »` : meta.newLabel}</DialogTitle>
          <DialogDescription>Enregistré directement dans la base Notion Écosystème La Mêlée.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {field("group-name", "Nom *", <Input id="group-name" value={form.name} onChange={set("name")} data-testid="group-form-name-input" />)}
          {field("group-type", "Type", <OptionPicker single options={options.type} value={form.type} onChange={set("type")} testId="group-form-type" />)}
          {field("group-description", "Description", <Textarea id="group-description" rows={3} value={form.description} onChange={set("description")} data-testid="group-form-description-input" />)}
          {field("group-color", "Couleur", <OptionPicker single options={options.color} value={form.color} onChange={set("color")} testId="group-form-color" />)}
          {field("group-parent", "Groupe parent", (
            <select id="group-parent" value={form.parent} onChange={set("parent")} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="group-form-parent-select">
              <option value="">Aucun</option>
              {parents.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          ))}
          {field("group-email", "Mail générique", <Input id="group-email" type="email" value={form.email} onChange={set("email")} data-testid="group-form-email-input" />)}
          {field("group-image", "Icône (URL)", <Input id="group-image" value={form.image_url} onChange={set("image_url")} placeholder="https://…" data-testid="group-form-image-input" />)}
          {field("group-status", "Statut", <OptionPicker single options={options.status} value={form.status} onChange={set("status")} testId="group-form-status" />)}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex items-center justify-between rounded-xl border border-border p-3 text-sm font-semibold">
              Mise en avant <Switch checked={form.featured} onCheckedChange={set("featured")} data-testid="group-form-featured-switch" />
            </label>
            <label className="flex items-center justify-between rounded-xl border border-border p-3 text-sm font-semibold">
              Visible dans l'annuaire <Switch checked={form.visible} onCheckedChange={set("visible")} data-testid="group-form-visible-switch" />
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="group-form-submit-button">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
